import joi from 'joi';
import z from 'zod';
import { $rewardType } from '../../rewards-api-client/rewards.schemas';
import {
  Address,
  CurrencyBalanceAttributes,
  GetProfileIncludeOptions,
  ProfileAttributes,
  ProfileIdentity,
  ProfileIdentityKind,
  ProfileState,
} from '../types/profile';
import { MembershipAttributes } from '../types/membership';
import {
  ProfileRewardAttributes,
  TransactionAttributes,
  TransactionI18nAttributes,
} from '../types/transaction';
import { RewardState } from '../../rewards-api-client/rewards.types';

export const profilePictureUrlSchema = joi.string().custom(val => new URL(val).href);
export const profileBirthdaySchema = joi.string().isoDate().empty('').allow(null);
export const profileLanguageSchema = joi.string().default('en').replace(/-.*$/, '');

export const profileAddressSchema = joi.object<Address>({
  street: joi.string().optional().allow(null),
  number: joi.string().optional().allow(null),
  box: joi.string().optional().allow(null),
  state: joi.string().optional().allow(null),
  city: joi.string().optional().allow(null),
  zipCode: joi.string().optional().allow(null),
  country: joi.string().optional().allow(null),
});

/**
 * `userName` was dropped from the data model, but public-api rejects unknown keys, so it stays
 * accepted (and discarded) on the wire to avoid breaking clients built against the old contract.
 */
type ProfileIdentityInput = ProfileIdentity & { userName?: unknown };

export const profileIdentitySchema = joi.object<ProfileIdentityInput>({
  kind: joi.string().invalid('id', 'profileId').required(),
  value: joi
    .string()
    .custom((value: string) => value.toLowerCase())
    .required(),
  userName: joi.any().optional().strip(),
  programId: joi.when('kind', {
    is: joi.string().valid(ProfileIdentityKind.referralCode, ProfileIdentityKind.memberCode),
    then: joi.string().uuid(),
    otherwise: joi.string().uuid().optional().allow(null),
  }),
});

export const profileIdentitiesSchema = joi.array().items(profileIdentitySchema);

export const $GetProfileIncludeSchema = z.array(z.nativeEnum(GetProfileIncludeOptions)).default([]);

export const currencyBalanceSchema = joi.object<CurrencyBalanceAttributes>({
  id: joi.string().uuid(),
  version: joi.number(),
  programId: joi.string().uuid(),
  currencyId: joi.string().uuid(),
  value: joi.number(),
  totalAccumulated: joi.number(),
  totalOverCurrencyPeriod: joi.number(),
  firstTimeEarned: joi.string(),
  createdAt: joi.string().optional().empty('').allow(null),
  updatedAt: joi.string().optional().empty('').allow(null),
  deletedAt: joi.string().optional().empty('').allow(null),
  nextExpiration: joi.object({ date: joi.string(), amount: joi.number() }).optional().allow(null),
});

export const membershipSchema = joi.object<MembershipAttributes>({
  id: joi.string().uuid(),
  programId: joi.string().uuid(),
  lastVisit: joi.string().optional().allow(null),
  totalDailyVisits: joi.number(),
  consecutiveDailyVisits: joi.number(),
  blocked: joi.boolean(),
  createdAt: joi.string(),
  level: joi
    .object({
      current: joi.string().uuid().optional().allow(null),
      acquiredAt: joi.string().optional().allow(null),
      expireAt: joi.string().optional().allow(null),
    })
    .optional()
    .allow(null),
});

export const transactionSchema = joi.object<TransactionAttributes>({
  id: joi.string().uuid(),
  accountId: joi.string(),
  programId: joi.string().uuid(),
  ruleId: joi.string().uuid().optional().allow(null),
  eventId: joi.string().optional().allow(null),
  i18nAttributes: joi.object<TransactionI18nAttributes>().pattern(
    /[a-z]+/i,
    joi.object({
      description: joi.string().required(),
    }),
  ),
  currencyId: joi.string().uuid(),
  amount: joi.number(),
  quantityUsed: joi.number(),
  available: joi.boolean(),
  eventTimestamp: joi.string(),
  createdAt: joi.string().optional().allow(null),
  updatedAt: joi.string().optional().allow(null),
  expirationDate: joi.string().optional().allow(null),
  expired: joi.boolean(),
});

export const profileRewardSchema = joi.object<ProfileRewardAttributes>({
  id: joi.string().uuid(),
  accountId: joi.string(),
  programId: joi.string().uuid(),
  profileVersion: joi.number(),
  state: joi.string().valid(...Object.values(RewardState)),
  ruleId: joi.string().uuid().optional().allow(null),
  rewardId: joi.string().uuid(),
  type: $rewardType,
  promocode: joi.string().optional().allow(null),
  promocodeId: joi.string().optional().allow(null),
  i18nAttributes: joi.object<TransactionI18nAttributes>().pattern(
    /[a-z]+/i,
    joi.object({
      name: joi.string().required(),
      description: joi.string().optional(),
      image: joi.string().required(),
    }),
  ),
  createdAt: joi.string().optional().allow(null),
  updatedAt: joi.string().optional().allow(null),
});

export const profileSchema = joi.object<ProfileAttributes>({
  id: joi.string().uuid(),
  numericalId: joi.number(),
  externalId: joi.string().optional().allow(null),
  accountId: joi.string(),
  version: joi.number(),
  state: joi.string().valid(...Object.values(ProfileState)),
  firstName: joi.string().optional().allow(null),
  lastName: joi.string().optional().allow(null),
  language: joi.string(),
  birthday: joi.string().optional().allow(null),
  picture: joi.string().optional().allow(null),
  email: joi.string(),
  currencies: joi.array().items(currencyBalanceSchema).optional().allow(null).default([]),
  address: profileAddressSchema.optional().allow(null),
  phone: joi.string().optional().allow(null),
  gender: joi.string().optional().allow(null),
  customKeys: joi
    .object<Record<string, number>>({})
    .pattern(joi.string(), joi.any())
    .optional()
    .allow(null),
  createdAt: joi.string().optional().allow(null),
  updatedAt: joi.string().optional().allow(null),
  identities: profileIdentitiesSchema.optional().allow(null),
  transactions: joi.array().items(transactionSchema).optional().allow(null),
  memberships: joi.array().items(membershipSchema).optional().allow(null),
  rewards: joi.array().items(profileRewardSchema).optional().allow(null),
});
